import { FrontendUserSchema } from '@fastre/core/schemas/userMembership'
import { useTheme } from '@mui/joy'
import Avatar from '@mui/joy/Avatar'
import Box from '@mui/joy/Box'
import List from '@mui/joy/List'
import ListDivider from '@mui/joy/ListDivider'
import ListItem from '@mui/joy/ListItem'
import ListItemContent from '@mui/joy/ListItemContent'
import ListItemDecorator from '@mui/joy/ListItemDecorator'
import Typography from '@mui/joy/Typography'
import { useConjunctionalAgenciesApi, useRolesApi, useUsersApi } from 'apiProviders'
import { useUserData } from 'auth'
import Loading from 'components/Loading'
import { groupBy, prop, propEq, sortBy } from 'ramda'
import { Fragment } from 'react'
import { useNavigate } from 'react-router'

const UserList = ({ users, sort }: { users: FrontendUserSchema[]; sort: 'first' | 'last' }) => {
    const navigate = useNavigate()
    const conjunctionalAgenciesApi = useConjunctionalAgenciesApi()

    const findConjunctional = (user: FrontendUserSchema) => {
        const conjId = user.conjunctionalAgencyId ?? user.salesAssistantConjunctionalAgencyId
        if (conjId) {
            return conjunctionalAgenciesApi.data?.find(propEq('conjunctionalAgencyId', conjId))?.licenseeName
        }
    }

    return sortBy(user => user[`${sort}Name`].toLowerCase(), users).map(user => (
        <List
            key={user.userId}
            size="sm"
            sx={{
                '--ListItem-paddingX': 0,
            }}
        >
            <ListItem
                component="a"
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'start',
                    cursor: 'pointer',
                    textDecoration: 'none',
                    ':hover': {
                        backgroundColor: 'var(--joy-palette-background-level2)',
                        borderRadius: 'sm',
                    },
                    px: 1,
                    pt: 1,
                }}
                //href={`users/${user.userId}`}
                onClick={() => navigate(user.userId)}
            >
                <ListItemContent
                    sx={{
                        display: 'flex',
                        gap: 4,
                        alignItems: 'start',
                    }}
                >
                    <ListItemDecorator>
                        <Avatar size="lg">
                            {user.profileImage ? (
                                <img
                                    src={user.profileImage}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                    }}
                                />
                            ) : (
                                `${user.firstName[0]}${user.lastName[0]}`
                            )}
                        </Avatar>
                    </ListItemDecorator>
                    <div>
                        <Typography
                            fontWeight={600}
                            gutterBottom
                        >
                            {user.firstName} {user.lastName}
                        </Typography>
                        <Typography
                            level="body-xs"
                            gutterBottom
                        >
                            {findConjunctional(user)}
                        </Typography>
                    </div>
                </ListItemContent>
            </ListItem>
            <ListDivider />
        </List>
    ))
}

export default ({ sort }: { sort: 'first' | 'last' }) => {
    const theme = useTheme()
    const usersApi = useUsersApi(true)
    const rolesApi = useRolesApi()
    const { user: userData } = useUserData()

    if (usersApi.data === undefined) {
        return <Loading />
    }

    return (
        <Box
            //sx={{ display: { xs: 'block', sm: 'none' } }}
            sx={{ maxWidth: theme.breakpoints.values.sm + 'px' }}
        >
            {userData.permissions.includes('users.view') ? (
                Object.entries(groupBy(prop('roleId'), usersApi.data)).map(([roleId, subUsers]) => (
                    <Fragment key={roleId}>
                        <Typography
                            level="h4"
                            sx={{ mt: 2, mb: 1 }}
                        >
                            {rolesApi.data?.find(role => role.id === roleId)?.roleName}
                        </Typography>
                        <UserList
                            users={subUsers}
                            sort={sort}
                        />
                    </Fragment>
                ))
            ) : (
                <UserList
                    users={usersApi.data.filter(
                        user =>
                            user.userId === userData.userId ||
                            (userData.salesAssistantLeadAgentId &&
                                userData.salesAssistantLeadAgentId === user.userId) ||
                            (userData.salesAssistantConjunctionalAgencyId &&
                                userData.salesAssistantConjunctionalAgencyId == user.conjunctionalAgencyId),
                    )}
                    sort={sort}
                />
            )}
        </Box>
    )
}
